'use client';

import Image from 'next/image';

import Skeleton from '@/app/(football)/[team]/_components/common/Skeleton';
import StickerCard from '@/app/(football)/[team]/_components/common/StickerCard';
import { useTeamConfig } from '@/app/(football)/[team]/_context/context';
import { cn } from '@/app/lib/utils/tailwindcss';

export const CARD_DECO_CONTAINER_WIDTH = 112;
export const CARD_DECO_CONTAINER_HEIGHT = 173;

const CDN_BASE = 'https://cdn.kottonseed.io/assets/football/2026';

interface CardDecoListItemProps {
  nickName: string;
  profileImage: string;
  image: string;
  width: number;
  height: number;
  stickers: Array<{
    idx: string;
    src: string;
    x: number;
    y: number;
    width: number;
    height: number;
    rotation: number;
  }>;
}

export default function CardDecoListItem({
  nickName,
  profileImage,
  image,
  width,
  height,
  stickers,
}: CardDecoListItemProps) {
  const { teamSlug } = useTeamConfig();
  const bgImageSrc = `${CDN_BASE}/${teamSlug}/main/card_deco_list_bg.webp`;

  return (
    <div
      className={cn(
        'isolate relative h-[229px] w-[136px] rounded',
        'flex flex-col items-center gap-3 p-3',
        'bg-card-deco-list-bg',
        'gradient-border gradient-border-card-deco-list'
      )}
    >
      {/* Background Image */}
      <Image
        src={bgImageSrc}
        alt=""
        fill
        sizes="136px"
        className="pointer-events-none -z-20 rounded object-cover"
      />

      {/* Card Container */}
      <div style={{ width: CARD_DECO_CONTAINER_WIDTH, height: CARD_DECO_CONTAINER_HEIGHT, flexShrink: 0 }}>
        <StickerCard
          mode="view"
          image={image}
          width={width}
          height={height}
          stickers={stickers}
          containerSize={{ width: CARD_DECO_CONTAINER_WIDTH, height: CARD_DECO_CONTAINER_HEIGHT }}
        />
      </div>

      {/* Profile */}
      <div className="flex w-full items-center gap-1.5">
        <div className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-[#d9d9d9]">
          <Image src={profileImage || '/images/@/@default.svg'} alt={`${nickName} profile`} width={20} height={20} />
        </div>
        <span className="typo-body-8 line-clamp-1 flex-1 overflow-hidden text-ellipsis text-white">{nickName}</span>
      </div>
    </div>
  );
}

// Skeleton component for loading state
export function CardDecoListItemSkeleton({ teamSlug }: { teamSlug: string }) {
  const bgImageSrc = `${CDN_BASE}/${teamSlug}/main/card_deco_list_bg.webp`;

  return (
    <div
      className={cn(
        'isolate relative h-[229px] w-[136px] rounded',
        'flex flex-col items-center gap-3 p-3',
        'bg-card-deco-list-bg',
        'gradient-border gradient-border-card-deco-list'
      )}
    >
      <Image
        src={bgImageSrc}
        alt=""
        fill
        sizes="136px"
        className="pointer-events-none -z-20 rounded object-cover"
      />
      <Skeleton width={CARD_DECO_CONTAINER_WIDTH} height={CARD_DECO_CONTAINER_HEIGHT} />
      <div className="flex w-full items-center gap-1.5">
        <Skeleton width={20} height={20} borderRadius={100} />
        <Skeleton width={70} height={14} />
      </div>
    </div>
  );
}
